import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DataService} from '../../../services/data.service';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {Budget, GeneratedBy} from '../../../interfaces';
import {BudgetModel} from '../../models/budget-model';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {AuthService} from '../../../services/auth.service';
import {CommonModule} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';


@Component({
  selector: 'app-assemblyCosts-form',
  templateUrl: './assemblyCosts-form.component.html',
  styleUrls: ['./assemblyCosts-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
  ]
})
export class AssemblyCostsFormComponent implements OnInit {

  @Output() updateCostSummary = new EventEmitter<string>();
  public assemblyCostsForm: UntypedFormGroup;
  private unsubscribe$ = new Subject();

  constructor(private dataService: DataService,
              private fb: UntypedFormBuilder,
              private auth: AuthService) {
  }

  private _budget: Budget;
  get budget() {
    return this._budget;
  }

  @Input() set budget(b: Budget) {
    this._budget = b;
    if (this.assemblyCostsForm) {
      this.refreshFormEnabled();
    }
  }

  private canAskProjectAuth = false;
  private canGrantProjectAuth = false;
  private canEditBudget = false;
  private canEditOtherBudget = false;

  ngOnInit() {

    // Definition of the assemblyCosts Form
    // build the form model
    this.assemblyCostsForm = this.fb.group({
      employee: [this._budget.assemblyCosts ? this._budget.assemblyCosts.employee : 0, [Validators.min(0), this.isInteger]],
      projectManager: [this._budget.assemblyCosts ? this._budget.assemblyCosts.projectManager : 0, [Validators.min(0), this.isInteger]],
      employeeDays: [this._budget.assemblyCosts ? this._budget.assemblyCosts.employeeDays : 0, Validators.min(0)],
      projectManagerDays: [this._budget.assemblyCosts ? this._budget.assemblyCosts.projectManagerDays : 0, Validators.min(0)],
      employeeCost: [this._budget.assemblyCosts ? this._budget.assemblyCosts.employeeCost : 0, Validators.min(0)],
      projectMangerCost: [this._budget.assemblyCosts ? this._budget.assemblyCosts.projectMangerCost : 0, Validators.min(0)],
      travelExpensesAccomodation:
        [this._budget.assemblyCosts ? this._budget.assemblyCosts.travelExpensesAccomodation : 0, Validators.min(0)],
      travelExpensesFlights: [this._budget.assemblyCosts ? this._budget.assemblyCosts.travelExpensesFlights : 0, Validators.min(0)],
      renting: [this._budget.assemblyCosts ? this._budget.assemblyCosts.renting : 0, Validators.min(0)],
      externalManpower: [this._budget.assemblyCosts ? this._budget.assemblyCosts.externalManpower : 0, Validators.min(0)],
      other: [this._budget.assemblyCosts ? this._budget.assemblyCosts.other : 0, Validators.min(0)]
    });

    /**
     * Auto save / commit enabled
     */
    this.assemblyCostsForm.valueChanges
      .pipe(debounceTime(500),
        takeUntil(this.unsubscribe$),
        distinctUntilChanged((val1, val2) => {
          return val1.employee === val2.employee &&
            val1.projectManager === val2.projectManager &&
            val1.employeeDays === val2.employeeDays &&
            val1.projectManagerDays === val2.projectManagerDays &&
            val1.employeeCost === val2.employeeCost &&
            val1.projectMangerCost === val2.projectMangerCost &&
            val1.travelExpensesAccomodation === val2.travelExpensesAccomodation &&
            val1.travelExpensesFlights === val2.travelExpensesFlights &&
            val1.renting === val2.renting &&
            val1.externalManpower === val2.externalManpower &&
            val1.other === val2.other;
        }))
      .subscribe(async () => {
        await this.submit();
      });

    this.setPermissionHandler();
    this.refreshFormEnabled();
  }

  setPermissionHandler() {
    this.auth.userHasPermissions([
      'app.budget.auth.ask',
      'app.budget.auth.grant',
      'app.budget.write',
      'app.budget.edit.others'
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(val => {
        this.canAskProjectAuth = val[0];
        this.canGrantProjectAuth = val[1];
        this.canEditBudget = val[2];
        this.canEditOtherBudget = val[3];
        this.refreshFormEnabled();
      });
  }

  refreshFormEnabled() {
    if (!this.assemblyCostsForm) return;

    if (BudgetModel.genericEditBudgetPermission(
      this.budget,
      this.auth.currentUser,
      this.canEditBudget,
      this.canEditOtherBudget)) {
      this.assemblyCostsForm.enable({emitEvent: false});
    } else {
      this.assemblyCostsForm.disable({emitEvent: false});
    }
  }


  isInteger = (control: UntypedFormControl) => {
    const val = control.value;
    if (Number.isInteger(val)) {
      return null;
    } else {
      return {
        isNotInteger: {value: val}
      };
    }
  }

  async submit() {
    const ac = {
      employee: this.assemblyCostsForm.value.employee,
      projectManager: this.assemblyCostsForm.value.projectManager,
      employeeDays: this.assemblyCostsForm.value.employeeDays,
      projectManagerDays: this.assemblyCostsForm.value.projectManagerDays,
      employeeCost: this.assemblyCostsForm.value.employeeCost,
      projectMangerCost: this.assemblyCostsForm.value.projectMangerCost,
      travelExpensesAccomodation: this.assemblyCostsForm.value.travelExpensesAccomodation,
      travelExpensesFlights: this.assemblyCostsForm.value.travelExpensesFlights,
      renting: this.assemblyCostsForm.value.renting,
      externalManpower: this.assemblyCostsForm.value.externalManpower,
      other: this.assemblyCostsForm.value.other
    };
    console.log('Assembly costs - submit', ac);

    try {
      await this.dataService.patchBudget(this._budget.id, {
        assemblyCosts: ac
      });

      await this.dataService.upsertSubBudget(this._budget.id, 'ASSEMBLY', {
        description: 'Installation',
        id: 'ASSEMBLY',
        overrideLock: true,
        created: firebase.firestore.FieldValue.serverTimestamp(),
        deleted: null,
        active: true,
        createdBy: undefined, // This will be filled by dataservice
        generatedBy: GeneratedBy.Assembly,
        calculate: true
      });
      await this.dataService.setSBActivity(this._budget.id, 'ASSEMBLY', 'projectManager', {
        activity: 'Project Manager Installation',
        category: 'INS',
        hours: ac.projectManager * ac.projectManagerDays * 8,
        cost: ac.projectMangerCost / 8
      });
      await this.dataService.setSBActivity(this._budget.id, 'ASSEMBLY', 'employee', {
        activity: 'Employee Installation',
        category: 'INS',
        hours: ac.employee * ac.employeeDays * 8,
        cost: ac.employeeCost / 8
      });
      await this.dataService.setSBMaterial(this._budget.id, 'ASSEMBLY', 'travelExpenses', {
        description: 'Travel Expenses Accomodation',
        category: 'INS',
        costEach: ac.travelExpensesAccomodation,
        quantity: 1,
        unit: 'pieces'
      });
      await this.dataService.setSBMaterial(this._budget.id, 'ASSEMBLY', 'flights', {
        description: 'Travel Expenses Flights',
        category: 'INS',
        costEach: ac.travelExpensesFlights,
        quantity: 1,
        unit: 'pieces'
      });
      await this.dataService.setSBMaterial(this._budget.id, 'ASSEMBLY', 'externalManpower', {
        description: 'External Manpower',
        category: 'INS',
        costEach: ac.externalManpower,
        quantity: 1,
        unit: 'pieces'
      });
      await this.dataService.setSBMaterial(this._budget.id, 'ASSEMBLY', 'renting', {
        description: 'Renting',
        category: 'INS',
        costEach: ac.renting,
        quantity: 1,
        unit: 'pieces'
      });
      await this.dataService.setSBMaterial(this._budget.id, 'ASSEMBLY', 'other', {
        description: 'Other',
        category: 'INS',
        costEach: ac.other,
        quantity: 1,
        unit: 'pieces'
      });
      console.log('Assembly costs - All updated', this.assemblyCostsForm);
      this.updateCostSummary.emit(this._budget.id);
    } catch (err) {
      console.error(err);
    }
  }

}
